import React, { useState, useEffect } from 'react';
import './PriceDebugPopup.css';

const PriceDebugPopup = ({ card, onClose, calculatePrice, userSettings }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [debugData, setDebugData] = useState(null);
  const [activeCondition, setActiveCondition] = useState('NM');
  const [error, setError] = useState(null);

  // Currency symbols for formatting prices
  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
    MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
  };

  const currencySymbol = currencySymbols[userSettings?.currency] || '$';

  useEffect(() => {
    const fetchDebugData = async () => {
      if (!card || !calculatePrice) {
        setError('Missing card or calculatePrice function');
        setIsLoading(false);
        return;
      }

      try {
        const conditions = ['NM', 'LP', 'MP', 'HP'];
        // Get available printings from card
        const printings = [...new Set(card.skus?.map(sku => sku.printingName) || ['Normal'])];
        const defaultPrinting = printings[0] || 'Normal';

        // Get available languages
        const languages = [...new Set(card.skus?.map(sku => sku.langAbbr) || ['EN'])];
        const defaultLanguage = languages.includes('EN') ? 'EN' : languages[0] || 'EN';

        const data = {};

        for (const condition of conditions) {
          // Call calculatePrice with collectDebugInfo = true
          const result = await calculatePrice(card, condition, defaultPrinting, defaultLanguage, true, true);
          data[condition] = {
            ...result,
            printing: defaultPrinting,
            language: defaultLanguage
          };
        }

        setDebugData(data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching debug data:', err);
        setError(err.message || 'Error calculating prices');
        setIsLoading(false);
      }
    };

    fetchDebugData();
  }, [card, calculatePrice]);

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    return parseFloat(price).toFixed(2);
  };

  const formatPercentage = (pct) => {
    if (pct === null || pct === undefined) return 'N/A';
    return `${(pct * 100).toFixed(0)}%`;
  };

  if (isLoading) {
    return (
      <div className="price-debug-popup">
        <div className="price-debug-content">
          <h2>Loading Price Debug Information...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="price-debug-popup">
        <div className="price-debug-content">
          <button className="close-button" onClick={onClose}>×</button>
          <h2>Error</h2>
          <p className="error-message">{error}</p>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  const activeData = debugData?.[activeCondition];
  const debugInfo = activeData?.debugInfo;

  return (
    <div className="price-debug-popup">
      <div className="price-debug-content">
        <button className="close-button" onClick={onClose}>×</button>
        <h2>Price Debug Information</h2>

        {/* Card Info Header */}
        <div className="card-info-header">
          <h3>{card.name}</h3>
          <p>{card.expansionName} | {card.gameName} | {card.rarity}</p>
          <p className="product-id">Product ID: {card.productId}</p>
        </div>

        {/* Condition Tabs */}
        <div className="condition-tabs">
          {['NM', 'LP', 'MP', 'HP'].map(cond => (
            <button
              key={cond}
              className={`condition-tab ${activeCondition === cond ? 'active' : ''}`}
              onClick={() => setActiveCondition(cond)}
            >
              {cond}
            </button>
          ))}
        </div>

        {/* Price Overview */}
        <div className="price-debug-section">
          <h4>Price Overview ({activeCondition})</h4>
          <div className="price-overview-grid">
            <div className="price-box cash">
              <span className="label">Cash Price</span>
              <span className="value">{currencySymbol}{formatPrice(activeData?.cashPrice)}</span>
              <span className="percentage">{formatPercentage(activeData?.cashPercentage)}</span>
              <span className={`availability ${activeData?.cashAvailable ? 'available' : 'unavailable'}`}>
                {activeData?.cashAvailable ? 'Available' : 'Not Available'}
              </span>
            </div>
            <div className="price-box credit">
              <span className="label">Credit Price</span>
              <span className="value">{currencySymbol}{formatPrice(activeData?.creditPrice)}</span>
              <span className="percentage">{formatPercentage(activeData?.creditPercentage)}</span>
              <span className={`availability ${activeData?.creditAvailable ? 'available' : 'unavailable'}`}>
                {activeData?.creditAvailable ? 'Available' : 'Not Available'}
              </span>
            </div>
            <div className="price-box base">
              <span className="label">Base Price</span>
              <span className="value">{currencySymbol}{formatPrice(activeData?.basePrice)}</span>
              <span className="source">{activeData?.priceSource || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Status Flags */}
        <div className="price-debug-section">
          <h4>Status Flags</h4>
          <div className="flags-grid">
            <div className={`flag ${activeData?.isHotlist ? 'active' : ''}`}>
              Hotlist: {activeData?.isHotlist ? 'Yes' : 'No'}
            </div>
            <div className={`flag ${activeData?.isFixedValue ? 'active' : ''}`}>
              Fixed Value: {activeData?.isFixedValue ? 'Yes' : 'No'}
            </div>
            <div className={`flag ${activeData?.isBulk ? 'active' : ''}`}>
              Bulk: {activeData?.isBulk ? 'Yes' : 'No'}
            </div>
            <div className={`flag ${activeData?.contactForPrice ? 'active' : ''}`}>
              Contact For Price: {activeData?.contactForPrice ? 'Yes' : 'No'}
            </div>
            {activeData?.inventoryModifier && (
              <div className="flag active">
                Inventory Modifier: {activeData.inventoryModifier}% (Count: {activeData.inventoryCount})
              </div>
            )}
          </div>
        </div>

        {/* Calculation Steps */}
        {debugInfo?.calculationSteps && debugInfo.calculationSteps.length > 0 && (
          <div className="price-debug-section">
            <h4>Calculation Steps</h4>
            <div className="calculation-steps">
              {debugInfo.calculationSteps.map((step, index) => (
                <div key={index} className="calculation-step">
                  <span className="step-number">{index + 1}</span>
                  <div className="step-content">
                    <span className="step-name">{step.step}</span>
                    {Object.keys(step).filter(k => k !== 'step' && k !== 'timestamp').length > 0 && (
                      <div className="step-details">
                        {Object.entries(step)
                          .filter(([key]) => key !== 'step' && key !== 'timestamp')
                          .map(([key, value]) => (
                            <span key={key} className="step-detail">
                              {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trend Prices - Show from debugInfo or card SKU data */}
        {(() => {
          // Try to get trend prices from debugInfo first, then from card SKUs
          const trendPrices = debugInfo?.trendPrices || {};
          const hasTrendData = trendPrices.lowPrice || trendPrices.marketPrice || trendPrices.midPrice;

          // If no trend data in debugInfo, try to get from card SKUs
          let skuPrices = null;
          if (!hasTrendData && card.skus && card.skus.length > 0) {
            const nmSku = card.skus.find(s => s.condAbbr === 'NM') || card.skus[0];
            if (nmSku) {
              skuPrices = {
                lowPrice: nmSku.lowPrice,
                midPrice: nmSku.midPrice,
                highPrice: nmSku.highPrice,
                marketPrice: nmSku.marketPrice,
                lowestListingPrice: nmSku.lowestListingPrice
              };
            }
          }

          const displayPrices = hasTrendData ? trendPrices : skuPrices;
          const priceSource = hasTrendData ? 'Debug Info' : (skuPrices ? 'SKU Data' : null);

          if (!displayPrices) return null;

          return (
            <div className="price-debug-section">
              <h4>Trend Prices (TCGPlayer){priceSource && ` - from ${priceSource}`}</h4>
              <table className="debug-table">
                <tbody>
                  <tr>
                    <td>Low Price:</td>
                    <td>{currencySymbol}{formatPrice(displayPrices.lowPrice)}</td>
                  </tr>
                  <tr>
                    <td>Mid Price:</td>
                    <td>{currencySymbol}{formatPrice(displayPrices.midPrice)}</td>
                  </tr>
                  <tr>
                    <td>High Price:</td>
                    <td>{currencySymbol}{formatPrice(displayPrices.highPrice)}</td>
                  </tr>
                  <tr>
                    <td>Market Price:</td>
                    <td>{currencySymbol}{formatPrice(displayPrices.marketPrice)}</td>
                  </tr>
                  <tr>
                    <td>Lowest Listing:</td>
                    <td>{currencySymbol}{formatPrice(displayPrices.lowestListingPrice)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* User Settings */}
        {debugInfo?.userSettings && (
          <div className="price-debug-section">
            <h4>User Settings</h4>
            <table className="debug-table">
              <tbody>
                <tr>
                  <td>Default Cash %:</td>
                  <td>{debugInfo.userSettings.defaultCashPercentage}%</td>
                </tr>
                <tr>
                  <td>Default Credit %:</td>
                  <td>{debugInfo.userSettings.defaultCreditPercentage}%</td>
                </tr>
                <tr>
                  <td>Floor Price:</td>
                  <td>{currencySymbol}{debugInfo.userSettings.floorPrice}</td>
                </tr>
                <tr>
                  <td>Currency:</td>
                  <td>{debugInfo.userSettings.currency}</td>
                </tr>
                {debugInfo.userSettings.price_preference_order && (
                  <tr>
                    <td>Price Preference:</td>
                    <td>{debugInfo.userSettings.price_preference_order.join(' → ')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Matched Pricing Rule */}
        {activeData?.matchedPricingRule && (
          <div className="price-debug-section">
            <h4>Matched Pricing Rule</h4>
            <table className="debug-table">
              <tbody>
                <tr>
                  <td>Game:</td>
                  <td>{activeData.matchedPricingRule.game}</td>
                </tr>
                {activeData.matchedPricingRule.rarity && (
                  <tr>
                    <td>Rarity:</td>
                    <td>{activeData.matchedPricingRule.rarity}</td>
                  </tr>
                )}
                <tr>
                  <td>Price Range:</td>
                  <td>{currencySymbol}{activeData.matchedPricingRule.min_value} - {currencySymbol}{activeData.matchedPricingRule.max_value}</td>
                </tr>
                <tr>
                  <td>Cash %:</td>
                  <td>{activeData.matchedPricingRule.cash_percentage}%</td>
                </tr>
                <tr>
                  <td>Credit %:</td>
                  <td>{activeData.matchedPricingRule.credit_percentage}%</td>
                </tr>
                {activeData.matchedPricingRule.use_fixed_value && (
                  <>
                    <tr>
                      <td>Fixed Cash Value:</td>
                      <td>{currencySymbol}{activeData.matchedPricingRule.cash_value}</td>
                    </tr>
                    <tr>
                      <td>Fixed Credit Value:</td>
                      <td>{currencySymbol}{activeData.matchedPricingRule.credit_value}</td>
                    </tr>
                  </>
                )}
                {activeData.matchedPricingRule.inventory_modifiers && activeData.matchedPricingRule.inventory_modifiers.length > 0 && (
                  <tr>
                    <td>Inventory Modifiers:</td>
                    <td>
                      {activeData.matchedPricingRule.inventory_modifiers.map((mod, idx) => (
                        <div key={idx} className="inventory-modifier-item">
                          Qty {mod.min_quantity || mod.min_value}-{mod.max_quantity || mod.max_value}: {mod.percentage}%
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Hotlist Info */}
        {debugInfo?.hotlistInfo?.isHotlist && (
          <div className="price-debug-section hotlist-section">
            <h4>Hotlist Information</h4>
            <table className="debug-table">
              <tbody>
                <tr>
                  <td>Hotlist Cash %:</td>
                  <td>{debugInfo.hotlistInfo.cashPercent}%</td>
                </tr>
                <tr>
                  <td>Hotlist Credit %:</td>
                  <td>{debugInfo.hotlistInfo.creditPercent}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* All Conditions Summary */}
        <div className="price-debug-section">
          <h4>All Conditions Summary</h4>
          <table className="conditions-summary-table">
            <thead>
              <tr>
                <th>Condition</th>
                <th>Cash</th>
                <th>Credit</th>
                <th>Base</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {['NM', 'LP', 'MP', 'HP'].map(cond => {
                const data = debugData?.[cond];
                return (
                  <tr key={cond} className={cond === activeCondition ? 'active-row' : ''}>
                    <td>{cond}</td>
                    <td>{currencySymbol}{formatPrice(data?.cashPrice)}</td>
                    <td>{currencySymbol}{formatPrice(data?.creditPrice)}</td>
                    <td>{currencySymbol}{formatPrice(data?.basePrice)}</td>
                    <td>
                      {data?.isBulk ? 'Bulk' :
                       data?.contactForPrice ? 'Contact' :
                       data?.cashAvailable || data?.creditAvailable ? 'Available' : 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="price-debug-footer">
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default PriceDebugPopup;
